import { damp } from '../core/math';
import { RNG, rr } from '../core/rng';
import { losBlocked } from '../world/arena';
import { P } from '../world/player';
import { endAttack, fire, telegraph, updatePull } from './enemy-atk';
/* ============================================================
   小怪的行為 —— 一套狀態機跑完 28 種
   ============================================================

   權威規格 `05-ENEMY.md` §6.3。**28 種攻擊完全不同，但預備一律是同兩件事**：
   發光變強、一個部件往攻擊的反方向拉。所以行為層也只需要一套：

     0 巡航 ── 進到射程且冷卻好了 ─→ 1 預備(tTel) ─→ 2 攻擊(tAtk) ─→ 3 恢復(tRec) ─┐
     ↑                                                                              │
     └──────────────────────────────────────────────────────────────────────────────┘

   `tel` 與 `atk` 是 0→1 的進度，**直接餵給造型的 `fn(P, o)`** ——
   所以「牠正在蓄力」這件事在畫面上是造型自己講的，不需要行為層畫任何東西。
   加上地面預警（`render/warn-fx.ts`）之後才算完整：造型講「牠要出手了」，
   地面圖形講「**你該往哪閃**」。

   攻擊載荷（動了會發生什麼）在 `sim/enemy-atk.ts`。拆開的理由是
   **狀態機是 28 隻共用的一份，載荷是會一直長的那一半**。

   ## 預備時間的下限是規則不是數值

   規格 §6.3：**預備不得短於 0.26 秒**。60fps 下只有 15 幀，玩家還沒轉頭
   就被打到了 —— 那不是難度，那是資訊沒送到。家族表的 `tTel` 已經全部
   符合這條（最短的 NADEL 正好 0.26），調數值時不要往下破。
   `elites.test.ts` 有一條逐隻掃過去的斷言守它。 */

/** 自走型的攻擊：攻擊期間**由載荷控制速度**，狀態機不要插手。
    漏掉一個的話那隻會在出手的瞬間被 `seek(0)` 煞停 —— 症狀是
   「動畫演了但沒有位移」，而且不會有任何錯誤。 */
var SELF_MOVED = { lunge: 1, spin: 1, jump: 1 };

/** 追擊。`want` > 0 靠近、< 0 後退、0 站定。 */
function seek(e, dt, nx, nz, want) {
  var sp = e.T.spd * (e.vSpd || 1) * (e.buffSpd || 1) * want;
  e.vel.x = damp(e.vel.x, nx * sp, 5, dt);
  e.vel.z = damp(e.vel.z, nz * sp, 5, dt);
}

/** 每一步跑一次。位移、地形、分離、接觸傷害仍然由 `sim/enemies.ts` 統一處理 ——
    這裡只決定「想往哪走」與「什麼時候出手」。 */
export function swarmAI(e, dt, nx, nz, dist, sh) {
  var T = e.T, A = T.atk, self = !!SELF_MOVED[A.kind];
  e.cd -= dt;
  updatePull(e, dt);

  /* 支援類的殘留時間。到期一定要**三個欄位都還原** —— 池化物件的欄位沒清，
     下一隻普通雜兵會頂著加成或減傷跑出來，而且零錯誤（M8 記過一次）。 */
  if (e.buffT > 0) {
    e.buffT -= dt;
    if (e.buffT <= 0) { e.buffDmg = 1; e.buffSpd = 1; e.buffArm = 1; }
  }

  /* 步態相位。造型的 `legSwing` 吃 `o.ph`（0–1 一個完整步態），
     `o.mv` 是移動量（站定時腿只留 22% 的擺幅，看起來像在原地踏）。

     **相位只在巡航時推進**（原型 `stepActor`：`tel`/`atk`/`rec` 三個狀態
     都不動 `o.ph`）—— 出手的時候腿還在走的話，預備動作等於沒有：
     整隻的動態被步態蓋過去，玩家讀不到「牠停下來了，要出手了」。
     係數 `0.34 + 0.66·mv` 也是原型的值。 */
  var v = Math.hypot(e.vel.x, e.vel.z);
  e.mv = Math.min(1, v / Math.max(0.5, T.spd));
  if (e.st === 0) e.ph = (e.ph + dt * T.gait * (0.34 + 0.66 * e.mv)) % 1;

  if (e.st === 0) {
    /* 巡航時 `tel`/`atk` **平滑退回 0 而不是瞬間歸零**（原型的 λ 是 9 / 11）。
       瞬間歸零會讓恢復期結束的那一幀出現一個彈跳。 */
    e.tel = damp(e.tel, 0, 9, dt); e.atk = damp(e.atk, 0, 11, dt);
    seek(e, dt, nx, nz, 1);
    /* 橙・直射會被地形擋（規格 §2.3），所以它要看得到才起手；
       其餘三種不查 LOS —— 範圍與支援本來就不需要直線。 */
    var los = A.kind === 'bolt' || A.kind === 'sweep' || A.kind === 'radial' || A.kind === 'pull';
    var ok = !los || !losBlocked(e.pos.x, e.pos.z, e.pos.x + nx * dist, e.pos.z + nz * dist, P.y);
    /* **玩家滯空時遠程更積極**（冷卻視同少 0.55 秒）。近戰打不到空中的你，
       所以「久飛」的代價必須由遠程收 —— 不加這一條的話飛起來就是免費的安全，
       而那會讓地面戰與地形設計整組貶值。只給直射／掃射／彈幕這些真的
       抬得起頭的攻擊，範圍與支援不吃。 */
    if (los && P.y > 3) e.cd -= 0.55 * dt;   // 冷卻走得比平常快 1.55 倍
    if (dist < T.rng && e.cd <= 0 && ok) {
      e.st = 1; e.stT = T.tTel; telegraph(e, nx, nz);
    }
  } else if (e.st === 1) {
    e.stT -= dt;
    e.tel = 1 - Math.max(0, e.stT) / T.tTel;
    /* 預備＝往攻擊的反方向拉。自走型真的往後縮（讀得出「牠要撲了」），
       其餘站定 —— 一邊蓄力一邊追過來的話預備動作等於沒有。 */
    seek(e, dt, nx, nz, self ? -0.4 : 0);
    if (e.stT <= 0) { e.st = 2; e.stT = T.tAtk; e.tel = 1; fire(e, nx, nz, sh); }
  } else if (e.st === 2) {
    e.stT -= dt;
    e.atk = 1 - Math.max(0, e.stT) / T.tAtk;
    if (!self) seek(e, dt, nx, nz, 0);
    if (e.stT <= 0) { e.st = 3; e.stT = T.tRec; e.atk = 1; endAttack(e); }
  } else {
    e.stT -= dt;
    /* 恢復期：`tel` 與 `atk` 都用**指數平滑**退回 0（原型的 λ 是 8 / 10），
       不是線性也不是瞬間歸零。造型的後座、噴口收縮、蓄力發光全靠這兩個值演 ——
       上一版 `tel` 整個攻擊＋恢復期都停在 1，於是每一隻打完之後
       都維持著「滿蓄力」的姿勢站在那裡，直到冷卻結束才啪一下彈回去。 */
    e.tel = damp(e.tel, 0, 8, dt);
    e.atk = damp(e.atk, 0, 10, dt);
    /* 撲完往後退一下。這是「預備往反方向拉」的對稱動作，
       而且它是玩家的還手窗口 —— 恢復期還繼續逼近的話，
       一群 NADEL 會變成貼在你身上的一團，走位沒有意義。 */
    seek(e, dt, nx, nz, self ? -0.2 : 0.35);
    if (e.stT <= 0) {
      e.st = 0;                       // `tel`/`atk` 交給巡航分支繼續 damp 回 0
      /* 攻擊之間的喘息。全部同步的話一群同型會變成整齊的齊射，
         那是編隊該做的事，不是雜兵預設該有的樣子。 */
      e.cd = rr(0.5, 1.5) + (self ? 0 : 0.8);
    }
  }
}

/** 生成時挑變體（規格 §5.3）。回傳的東西全部由 `spawnEnemy` 寫進 instance。

    變體只改比例（`q`）與行為數值（`sp` 速度、`sc` 體型），
    **不新增 primitive、不改顏色** —— 加 primitive 會讓該型別多一顆
    InstancedMesh 而且所有變體都要付；改顏色會讓四色語彙說謊。 */
export function rollVariant(T, weights) {
  var r = RNG.next(), acc = 0, i = 0;
  for (; i < weights.length - 1; i++) { acc += weights[i]; if (r <= acc) break; }
  var V = T.vars[Math.min(i, T.vars.length - 1)];
  var q = {};
  for (var k in T.q0) q[k] = T.q0[k];
  for (var j in V.q) q[j] = V.q[j];
  return { vi: i, q: q, sp: V.sp, sc: V.sc };
}
